import {ReactElement, useState} from 'react';
import TextField from "../../ui/TextField";
import CheckboxWithState from "../../ui/CheckboxWithState";
import * as DateUtil from "../../lib/dateutils";
import store from '../../store/store';
import FinancialHelper from '../FinancialHelper';
import OptionHelper from '../../Configuration/GeneralOptions/OptionHelper';
import util from '../../lib/util';
import newHRTimestamp from "../../lib/picos/timestamp";
import {formatFML, formatFMLFromId} from "../../Contacts/ContactFormat";
import {getCurrentFirm, getCurrentUserId} from "../../lib/picos/currentLogin";
import {saveManyDiscountsQueue} from "../../store/actions/discountActions";
import Invoice from "../../entities/invoice";
import find from "../../store/find";
import Contact from "../../entities/contact";
import {confirmWarningOkCancelWithSelection} from "../../ui/Confirm";
import Alert from "../../lib/alert";
import {sendEmails} from "../../rest/api";
import {getEmails as externalGetEmails} from "../../lib/picos/getResourceTypes";
import Resource from "../../entities/resource";
import Task from "../../entities/task";
import Expense from "../../entities/expense";
import Transaction from "../../entities/transaction";
import FlatFee from "../../entities/flatFee";
import FlatFeeMilestone from "../../entities/flatFeeMilestone";
import {uuid} from "../../@types/schema";
import Matter from "../../entities/matter";
import {existsFilter} from "../../lib/picos/filters";
import LookupRegistry from '../../ui/LookupSelect/LookupRegistry';
import {EmailAttachment} from "../../@types/email";
import { cleanText, generateInvoicePdf } from './invoicePdf';
import { buildFinalValues } from './buildFinalValues';
import {
    finalizeInvoice as finalizeInvoiceAssoc,
    unfinalizeInvoice as unfinalizeInvoiceAssoc,
    commitInvoiceEntityChanges,
    AssociableEntitySet,
} from './invoiceAssociation';
import { addDays } from 'date-fns';

class InvoiceHelper {
    /**
     * Give a quick description of the transaction, like:
     *
     * "Payment from trust" or "Payment from client"
     *
     * @param {Transaction} tr
     * @returns {string}
     */
    static renderTransactionDescription = (tr: Transaction): string => {
        if (tr.txType === 'SA') return 'Balance Forward';
        else if (tr.txType === 'PT') {
            if (tr.sourceType === 'M') return 'Payment from Case Trust';
            else return 'Payment from Client Trust';
        }
        else if (tr.txType === 'PC') {
            const payerName = formatFMLFromId(tr.source!);
            return `Payment from ${payerName}`;
        }
        else if (['BA', 'ST', 'SA'].includes(tr.txType)) return 'Adjustment';
        else return 'Payment';
    };

    /**
     * @param {Expense} expense
     * @returns {ReactElement}
     */
    static renderExpenseDescription = (expense: Expense): ReactElement | null => {
        return expense.description ?
               <div className='invoice-summary__clipped-notes'
                    dangerouslySetInnerHTML={TextField.createMarkup(expense.description)}
               /> : null;
    };

    /**
     * @param {Expense} expense
     * @returns {string|null}
     */
    static renderDate = (expense: Expense): string | null => {
        return DateUtil.formatDate(expense.expenseDate, true, false, false);
    };

    /**
     * @param {Expense} expense
     * @returns {string|null}
     */
    static renderPayee = (expense: Expense): string | null => {
        if (expense.payee) {
            return formatFMLFromId(expense.payee, false);
        }
        else return null;
    };

    /**
     * Get the invoice from its id
     * @param {uuid} invoiceId
     * @returns {Invoice|null}
     */
    static getInvoiceFromId = (invoiceId: uuid): Invoice | null => {
        return store.getState().invoices.find(invoice => invoice.id === invoiceId);
    };

    /**
     * Toggle (un)finalization of an invoice. We need to return the changed invoice back otherwise the InvoiceCard
     * will not update until the changes are saved to the server!
     *
     * @param {Invoice} invoice
     * @returns {Promise<Invoice>}
     */
    static onToggleFinalize = util.bouncify(async (invoice: Invoice): Promise<Invoice> => {
        // Are we finalizing or un-finalizing this invoice?
        let isFinalizing = invoice.invoiceStatus === 'D' && !invoice.finalizedOn;
        let isUnfinalizing = invoice.invoiceStatus === 'F' && !!invoice.finalizedOn;

        // Sanity check (preserve historical handling of inconsistent input)
        if (!isFinalizing && !isUnfinalizing) {
            console.warn('Cleaning up invoice on toggle', invoice.invoiceStatus, invoice.finalizedOn);
            if (invoice.invoiceStatus === 'D') {
                invoice.finalizedOn = null;
                isFinalizing = true;
            }
            else if (invoice.invoiceStatus === 'F') isUnfinalizing = true;
            else if (invoice.finalizedOn) isUnfinalizing = true;
            else isFinalizing = true;
        }

        /* OWA-299 — gather info BEFORE the state transition so getInvoiceInfo sees the
         * current invoice status (it cares about finalized vs draft for some fields). */
        const info = FinancialHelper.getInvoiceInfo(invoice)!;

        /**
         * State transition via the paired-association helpers. These handle the
         * forward (*Ids), back-pointer (entity.invoiceId), and status flip in lockstep.
         * See ./invoiceAssociation.ts and ./INVOICE_ASSOCIATION_INVARIANT.md.
         */
        const result: { invoice: Invoice; entities: AssociableEntitySet } = isUnfinalizing
            ? unfinalizeInvoiceAssoc(invoice)
            : finalizeInvoiceAssoc(invoice, {
                taskIds: info.tasks.map(t => t.id),
                expenseIds: info.expenses.map(e => e.id),
                transactionIds: info.transactions.map(tx => tx.id),
                flatFeeIds: info.flatFeeInfo.flatFees.filter(ff => ff.earnedThisPeriod).map(ff => ff.id),
                flatFeeMilestoneIds: info.flatFeeInfo.flatFeeMilestones.filter(ffm => ffm.earnedThisPeriod).map(ffm => ffm.id),
            });

        const inv = result.invoice;

        /**
         * Business-field tailoring on the returned invoice clone. The helpers handle
         * association and status; everything below is invoice-state computation that
         * lives outside the association concern.
         */
        if (isFinalizing) {
            inv.amountPaid = info.paymentTotal;
            inv.totalBilled = info.currentInvoiceAmount;
            inv.totalDue = info.amountDue;
            if (inv.terms != null) inv.dueDate = addDays(inv.issueDate, inv.terms);

            const optionHelper = new OptionHelper();
            inv.billingIncrement = optionHelper.getOptionValue(OptionHelper.BILLING_INCREMENT) as number;
            inv.roundingMethod = optionHelper.getOptionValue(OptionHelper.ROUNDING_METHOD) as string;

            // OWA-996: freeze line hours and costs with the same increment and rounding the totals
            // above used (the firm's options), not the draft's own values, which default to 1 minute.
            const snapshotSource = invoice.clone();
            snapshotSource.billingIncrement = inv.billingIncrement;
            snapshotSource.roundingMethod = inv.roundingMethod;
            inv.finalValues = buildFinalValues(snapshotSource, info);
        }
        // un-finalize: unfinalizeInvoice cleared finalValues / totals already.

        /**
         * Recalculate percentage-based discount totals. Discounts use entityType + entityId
         * (not invoiceId) — see INVOICE_ASSOCIATION_INVARIANT.md — but the store mapping is
         * still keyed by 'invoiceId' as a derived field. Discounts persist across F/D toggles.
         */
        const discounts = store.getState()
            .discounts
            .getMappingFor('invoiceId', inv.id)
            .map(d => {
                const discount = d.clone();

                if (discount.method === 'P') {
                    const {taskTotal, expenseTotal, flatFeeInfo} = info;
                    const pct = discount.percentage! / 100.0;
                    let total = 0;
                    if (discount.applyToLabor) total += (taskTotal + flatFeeInfo.earnedAmount) * pct;
                    if (discount.applyToExpenses) total += expenseTotal * pct;

                    if (discount.total !== total) {
                        discount.total = total;
                        discount.status = 'U';
                        discount.addedBy = getCurrentUserId();
                        discount.addedOn = newHRTimestamp();
                    }
                }

                return discount;
            });

        if (discounts.length > 0) store.dispatch(saveManyDiscountsQueue(discounts));

        // Commit the invoice + entity changes in one batched tick.
        commitInvoiceEntityChanges(result);

        return inv;
    }, 500);

    static getClientEmails = (invoice: Invoice): Resource[] => {
        const useClients = OptionHelper.getOptionValueStatic(OptionHelper.EMAIL_CLIENT);
        if (!useClients) return [];
        return invoice.clientIds.map(cid => find<Contact>('contacts', cid))
            .map(c => externalGetEmails(c!).filter(e => e.canContact && e.isPrimary))
            .flat();
    }

    static getBenefactorEmails = (invoice: Invoice): Resource[] => {
        const benefactors: Resource[] = [];
        const matters = invoice.matterIds.map(mid => find<Matter>('matters', mid));
        const entityIds: uuid[] = [];
        matters.forEach(m => entityIds.push(m!.id));
        if (invoice.leadId) matters.forEach(m => entityIds.push(m!.leadId as uuid));
        entityIds.forEach(id => {
            store.getState().participants
                .getMappingFor('entityId', id)
                .filter(p => p.relationships?.includes(LookupRegistry.SACROSANCT.BENEFACTOR))
                .map(p => find<Contact>('contacts', p.contactId))
                .map(c => externalGetEmails(c!).filter(e => e.canContact && e.isPrimary))
                .flat()
                .filter(existsFilter)
                .forEach(r => benefactors.push(r));
        });
        return benefactors;
    }

    static getEmails = (invoice: Invoice): Resource[] => {
        const clients = this.getClientEmails(invoice);
        const useBenefactors = OptionHelper.getOptionValueStatic(OptionHelper.EMAIL_PAYER);
        const benefactors = useBenefactors ? this.getBenefactorEmails(invoice) : [];
        return clients.concat(benefactors);
    }

    static onSendEmail = (invoice: Invoice) => {
        if (!(invoice instanceof Invoice)) throw Error(`Expected an invoice`);

        const clientEmails = this.getClientEmails(invoice);
        const benefactorEmails = this.getBenefactorEmails(invoice);
        const defaultIncludeBenefactors = OptionHelper.getOptionValueStatic(OptionHelper.EMAIL_PAYER) && benefactorEmails.length > 0;
        const choice = { includeBenefactors: defaultIncludeBenefactors };

        const clientFirstNames = invoice.clientIds.map(cid => find<Contact>('contacts', cid)).map(c => c!.nick || c!.first).join(' and ');
        const firm = getCurrentFirm();

        const DialogBody = () => {
            const [includeBenefactors, setIncludeBenefactors] = useState(defaultIncludeBenefactors);
            choice.includeBenefactors = includeBenefactors;

            const recipients = (includeBenefactors ? clientEmails.concat(benefactorEmails) : clientEmails).map(r => r.value);

            return (
                <div>
                    <p>Sending invoice to:
                        <ul>{recipients.map(e => <li key={e} style={{fontWeight: 500}}>{e}</li>)}</ul>
                    </p>
                    {benefactorEmails.length > 0 && (
                        <p>
                            <CheckboxWithState
                                checked={defaultIncludeBenefactors as boolean}
                                onClick={() => setIncludeBenefactors(v => !v)}
                                label={`Include benefactor${benefactorEmails.length > 1 ? 's' : ''} (${benefactorEmails.map(r => r.value).join(', ')})`}
                            />
                        </p>
                    )}
                    {firm.sendToEmail && <p>Copying:
					<ul>
						<li style={{fontWeight: 500}}>{firm.sendToEmail}</li>
					</ul>
				</p>}
                    <p>Which style of invoice would you like send out?</p>
                </div>
            );
        };

        confirmWarningOkCancelWithSelection<string>(
            'Email Invoice(s)',
            <DialogBody />,
            [{
                label: 'Narrative (focus on the story)',
                value: 'N'
            }, {
                label: 'Detailed (focus on the costs)',
                value: 'D'
            }],
            'ok',
            'cancel',
            async (mode: 'N' | 'D') => {
                try {
                    const emails = (choice.includeBenefactors ? clientEmails.concat(benefactorEmails) : clientEmails).map(r => r.value);

                    // Generate the invoice PDF using pdfmake
                    const { dataUrl, filename } = await generateInvoicePdf(invoice, { mode });

                    const attachment: EmailAttachment = {
                        filename,
                        content: dataUrl.split(',')[1],
                        contentType: "application/pdf",
                        disposition: "attachment",
                        encoding: 'base64'
                    };

                    // The description is rich text (HTML); flatten it to readable
                    // plain text for the text/plain part so tags don't leak. OWA-506.
                    const descriptionText = cleanText(invoice.description || '');

                    const text = `
${clientFirstNames},

${descriptionText}

Thanks,

Your team at ${getCurrentFirm().legalName}`;

                    const html = `
<html lang="en">
<head>
    <style>
    html {
        margin: .5in;
    }
    div {
        margin-bottom: 1em;
    }
    .thanks {
        margin-bottom: 2em;
    }
    </style>
    <title>Invoice ${invoice.invoiceNumber} for ${clientFirstNames}</title>
</head>
<body>
    <div>${clientFirstNames},</div>
    <div style="white-space: pre-wrap">${invoice.description || ''}</div>
    <div class="thanks">Thanks,</div>
    <div>Your team at ${getCurrentFirm().legalName}</div>
</body>
</html>
`;

                    const currentFirm = getCurrentFirm();
                    sendEmails({
                        from: 'support@outlawpractice.com',
                        to: emails.concat(currentFirm.sendToEmail ? [currentFirm.sendToEmail] : []),
                        cc: currentFirm.ccToEmail ? [currentFirm.ccToEmail] : [],
                        bcc: currentFirm.bccToEmail ? [currentFirm.bccToEmail] : [],
                        subject: `Your invoice from ${currentFirm.billingName || currentFirm.legalName}`,
                        text,
                        html,
                        attachments: [attachment]
                    })
                        .then((result) => {
                            if (result.isOk())
                                Alert.addSimpleSuccess('Email successfully sent to ' + emails.concat(currentFirm.sendToEmail || '').join(', '));
                            else
                                Alert.addSimpleError(`Unable to send emails: ${result.unwrapErr().message}`);
                        })
                        .catch((err: any) => {
                            Alert.addSimpleError(`Email failed to send: ${err.message}`);
                        });
                }
                catch (err) {
                    console.error('Failed to generate invoice PDF:', err);
                    Alert.addSimpleError(err.message || 'Failed to generate invoice PDF');
                }
            },
            () => {
                // Cancel
            }
        )
    }
}

export default InvoiceHelper;
